import { useApi } from "~/composables/useApi";
import { useToast } from "~/composables/useToast";
import type { ComputedRef, Ref } from "vue";
import type { AnyDocumentTypeHandler } from "../handlers/handler";
import { validateFileSizes, validateFileTypes } from "../utils";
import type DocumentVerificationDialog from "./DocumentVerificationDialog.vue";

export type UseDocumentUploadOptions = {
  adapter: Ref<AnyDocumentTypeHandler | null> | ComputedRef<AnyDocumentTypeHandler | null>;
  context: Ref<Record<string, unknown>> | ComputedRef<Record<string, unknown>>;
  onUploaded?: (documentId: string) => void;
  onVerified?: (documentId: string) => void;
  onRejected?: (documentId: string) => void;
};

export type UseDocumentUploadReturn = {
  // Files state
  files: Ref<File[]>;
  fileLimitWarning: Ref<string | undefined>;
  fileMimeTypeWarning: Ref<string | undefined>;
  dismissFileLimitWarning: () => void;
  dismissFileMimeTypeWarning: () => void;

  // Extraction state
  extractedData: Ref<Record<string, unknown>>;
  extractionError: Ref<string | undefined>;
  isExtracting: Ref<boolean>;

  // Dialog state
  showVerificationDialog: Ref<boolean>;
  verificationDialogRef: Ref<InstanceType<typeof DocumentVerificationDialog> | undefined>;

  // Storage state
  isStoring: Ref<boolean>;
  isRejecting: Ref<boolean>;

  // Rejection dialog state
  showRejectionDialog: Ref<boolean>;
  pendingRejectionData: Ref<Record<string, unknown>>;

  // Event handlers
  handleNewFiles: (newFiles: File[]) => void;
  handleRemoveFile: (index: number) => void;
  handleVerificationComplete: (verifiedData: Record<string, string>) => Promise<void>;
  handleVerificationReject: (currentData: Record<string, unknown>) => void;
  handleRejectionConfirm: (reason: string) => Promise<void>;
  handleRejectionCancel: () => void;
  handleVerificationRequestClose: () => void;

  // Confirmation dialog state
  showConfirmDialog: Ref<boolean>;
  handleConfirmClose: () => void;
  handleCancelClose: () => void;

  // Reset
  resetFlow: () => void;

  // Verification flow (for existing unverified documents)
  openVerificationDialog: (
    initialFiles: File[],
    initialExtractedData?: Record<string, unknown>,
    replacesDocumentId?: string
  ) => void;
  isVerificationMode: Ref<boolean>;

  // Replacement flow (for replacing verified documents)
  setDocumentToReplace: (documentId: string) => void;
};

const plural = (count: number, word: string, suffix = "s"): string => {
  return count > 1 ? `${word}${suffix}` : word;
};

export const useDocumentUpload = (options: UseDocumentUploadOptions): UseDocumentUploadReturn => {
  const { adapter, context, onUploaded, onVerified, onRejected } = options;

  const { addToastSuccess, addToastError, addToastWarning } = useToast();
  const { invoke } = useApi();

  // Dialog ref for force close
  const verificationDialogRef = ref<InstanceType<typeof DocumentVerificationDialog> | undefined>();

  // Dialog state
  const showVerificationDialog = ref(false);
  const showConfirmDialog = ref(false);

  // Files state
  const files = ref<File[]>([]);
  const fileLimitWarning = ref<string | undefined>();
  const fileMimeTypeWarning = ref<string | undefined>();

  // Extraction state
  const extractedData = ref<Record<string, unknown>>({});
  const extractionError = ref<string | undefined>();
  const isExtracting = ref(false);

  // Storage state
  const isStoring = ref(false);
  const isRejecting = ref(false);

  // Rejection dialog state
  const showRejectionDialog = ref(false);
  const pendingRejectionData = ref<Record<string, unknown>>({});

  // Verification mode (for existing unverified documents)
  const isVerificationMode = ref(false);
  // Track initial file count to know when new files are added in verification mode
  const initialFileCount = ref(0);
  // Track document being replaced (for verification of existing unverified documents)
  const replacesDocumentId = ref<string | undefined>();

  /**
   * Run OCR extraction on the given files.
   */
  const runExtraction = async (filesToExtract: File[], abortSignal?: AbortSignal): Promise<void> => {
    if (!adapter.value || filesToExtract.length === 0) return;

    extractionError.value = undefined;
    isExtracting.value = true;

    try {
      const extraction = await adapter.value.extractFromFiles(filesToExtract, { signal: abortSignal });
      const formData = adapter.value.transformExtraction(extraction);
      extractedData.value = formData;
      extractionError.value = undefined;
      isExtracting.value = false;
    } catch (error) {
      if (abortSignal?.aborted) return;
      console.error("OCR extraction failed:", error);
      extractionError.value = error instanceof Error ? error.message : "Une erreur est survenue lors de l'extraction";
      extractedData.value = {};
      isExtracting.value = false;
    }
  };

  const dismissFileLimitWarning = () => {
    fileLimitWarning.value = undefined;
  };

  const dismissFileMimeTypeWarning = () => {
    fileMimeTypeWarning.value = undefined;
  };

  /**
   * Process files for initial selection (not adding to existing).
   * Validates types, truncates to max, sets warnings, and opens dialog.
   */
  const processInitialFiles = (newFiles: File[]) => {
    if (!adapter.value) return;

    const { valid: validFiles, invalid: invalidFiles } = validateFileTypes(newFiles, adapter.value.acceptedMimeTypes);

    // Show warning for invalid files
    if (invalidFiles.length > 0) {
      const invalidNames = invalidFiles.map((f) => f.name).join(", ");
      fileMimeTypeWarning.value = `Fichier(s) non autorisé(s) ignoré(s): ${invalidNames}.`;
    } else {
      fileMimeTypeWarning.value = undefined;
    }

    if (validFiles.length === 0) return;

    let selectedFiles = validFiles;

    // Truncate if exceeds max files
    if (selectedFiles.length > adapter.value.maxFiles) {
      const originalCount = selectedFiles.length;
      selectedFiles = selectedFiles.slice(0, adapter.value.maxFiles);
      fileLimitWarning.value = `Maximum de ${adapter.value.maxFiles} ${plural(adapter.value.maxFiles, "fichier")} ${plural(adapter.value.maxFiles, "autorisé")}. Seuls les ${adapter.value.maxFiles} premiers fichiers ont été conservés (${originalCount} fichiers initialement sélectionnés).`;
    } else {
      fileLimitWarning.value = undefined;
    }

    files.value = selectedFiles;
    showVerificationDialog.value = true;
  };

  /**
   * Add files to an existing selection (dialog already open).
   * Handles slot limits and sets appropriate warnings.
   */
  const addFilesToExisting = (validFiles: File[]) => {
    if (!adapter.value) return;

    const remainingSlots = adapter.value.maxFiles - files.value.length;

    if (remainingSlots <= 0) {
      fileLimitWarning.value = `Maximum de ${adapter.value.maxFiles} ${plural(adapter.value.maxFiles, "fichier")} déjà atteint.`;
      return;
    }

    const filesToAdd = validFiles.slice(0, remainingSlots);
    files.value = [...files.value, ...filesToAdd];

    if (validFiles.length > remainingSlots) {
      fileLimitWarning.value = `${plural(filesToAdd.length, "Seul", "s")} ${filesToAdd.length} ${plural(filesToAdd.length, "fichier")} ${plural(filesToAdd.length, "ajouté")} (maximum atteint : ${adapter.value.maxFiles} fichiers).`;
    } else {
      fileLimitWarning.value = undefined;
    }
  };

  // Watch files and trigger extraction ONLY when files are added (not removed)
  watch(
    files,
    async (newFiles, oldFiles, onCleanup) => {
      if (!adapter.value) return;

      const newCount = newFiles.length;
      const oldCount = oldFiles?.length ?? 0;

      // Only extract if:
      // 1. File count increased (files were added)
      // 2. Verification dialog is open
      // 3. In verification mode: only extract if files were added beyond the initial set
      const shouldExtract =
        newCount > oldCount &&
        showVerificationDialog.value &&
        (!isVerificationMode.value || newCount > initialFileCount.value);

      if (shouldExtract) {
        const abortController = new AbortController();
        onCleanup(() => abortController.abort("Extraction cancelled"));
        await runExtraction(newFiles, abortController.signal);
      }
    },
    { deep: true }
  );

  const handleNewFiles = (selectedFiles: File[]) => {
    if (!adapter.value || selectedFiles.length === 0) return;

    // A toast, since the dialog holding the warning banners may not be open yet
    const { valid: newFiles, rejectionMessages } = validateFileSizes(selectedFiles);
    if (rejectionMessages.length > 0) {
      addToastWarning({ title: "Fichier(s) ignoré(s)", description: rejectionMessages.join("\n") });
    }
    if (newFiles.length === 0) return;

    if (showVerificationDialog.value) {
      // Adding to existing selection — validate types and add
      const { valid: validFiles, invalid: invalidFiles } = validateFileTypes(newFiles, adapter.value.acceptedMimeTypes);

      if (invalidFiles.length > 0) {
        const invalidNames = invalidFiles.map((f) => f.name).join(", ");
        fileMimeTypeWarning.value = `Fichier(s) non autorisé(s) ignoré(s): ${invalidNames}.`;
      } else {
        fileMimeTypeWarning.value = undefined;
      }

      if (validFiles.length > 0) {
        addFilesToExisting(validFiles);
      }
    } else {
      // Initial selection
      processInitialFiles(newFiles);
    }
  };

  const handleRemoveFile = (index: number) => {
    files.value = files.value.filter((_, i) => i !== index);

    // Reset state when all files are removed
    if (files.value.length === 0) {
      extractionError.value = undefined;
      fileLimitWarning.value = undefined;
      fileMimeTypeWarning.value = undefined;
      // Reset initial file count so newly added files trigger extraction
      initialFileCount.value = 0;
    }
  };

  const resetFlow = () => {
    showVerificationDialog.value = false;
    files.value = [];
    extractedData.value = {};
    extractionError.value = undefined;
    isExtracting.value = false;
    fileLimitWarning.value = undefined;
    fileMimeTypeWarning.value = undefined;
    isVerificationMode.value = false;
    initialFileCount.value = 0;
    replacesDocumentId.value = undefined;
    showRejectionDialog.value = false;
    pendingRejectionData.value = {};
  };

  /**
   * Open the verification dialog with pre-populated files and extracted data.
   * Used for verifying existing unverified documents.
   * If no initial data is provided, triggers OCR extraction automatically.
   */
  const openVerificationDialog = (
    initialFiles: File[],
    initialExtractedData?: Record<string, unknown>,
    documentIdToReplace?: string
  ) => {
    isVerificationMode.value = true;
    initialFileCount.value = initialFiles.length;
    replacesDocumentId.value = documentIdToReplace;
    files.value = initialFiles;
    showVerificationDialog.value = true;

    const hasInitialData = initialExtractedData && Object.keys(initialExtractedData).length > 0;
    if (hasInitialData) {
      extractedData.value = initialExtractedData;
    } else {
      // No initial data - trigger OCR extraction
      runExtraction(initialFiles);
    }
  };

  /**
   * Set the document ID to replace before user selects new files.
   * Used for replacing existing verified documents.
   */
  const setDocumentToReplace = (documentId: string) => {
    replacesDocumentId.value = documentId;
  };

  const handleVerificationComplete = async (verifiedData: Record<string, string>) => {
    if (!adapter.value) return;

    isStoring.value = true;
    try {
      // Call adapter store with context and original extraction data
      const { documentId, externalReview } = await adapter.value.store(
        files.value,
        verifiedData,
        context.value,
        extractedData.value,
        replacesDocumentId.value
      );

      // Success - call appropriate callback based on mode
      if (isVerificationMode.value) {
        onVerified?.(documentId);
      } else {
        onUploaded?.(documentId);
      }
      showVerificationDialog.value = false;

      // Show outcome-aware toast before resetting so we can still read the adapter's copy.
      const toastMessages = adapter.value.config.toastMessages;
      if (!externalReview) {
        addToastSuccess({ description: toastMessages.success });
      } else if (externalReview.status === "in_progress") {
        addToastSuccess({
          description: toastMessages.externalReviewStarted ?? "Le document a été envoyé pour validation externe.",
        });
      } else {
        addToastError({
          description:
            toastMessages.externalReviewFailed ?? "Une erreur est survenue lors de l'envoi pour validation externe.",
        });
      }

      resetFlow();
    } catch (error) {
      console.error("Document storage failed:", error);
      // Keep dialog open so user can retry or cancel
      addToastError({ title: "Une erreur est survenue lors de l'enregistrement du document" }, error);
    } finally {
      isStoring.value = false;
    }
  };

  const handleVerificationRequestClose = () => {
    // Always show confirmation on verification step (user may have filled form)
    showConfirmDialog.value = true;
  };

  const handleVerificationReject = (currentData: Record<string, unknown>) => {
    pendingRejectionData.value = currentData;
    showRejectionDialog.value = true;
  };

  const handleRejectionCancel = () => {
    showRejectionDialog.value = false;
    pendingRejectionData.value = {};
  };

  const handleRejectionConfirm = async (reason: string) => {
    if (!replacesDocumentId.value) {
      addToastError({ description: "Document introuvable" });
      return;
    }

    const documentId = replacesDocumentId.value;
    isRejecting.value = true;

    try {
      await invoke({
        path: "document/:id/internal-review/reject",
        method: "POST",
        params: { id: documentId },
        body: {
          rejection_reason: reason,
          data: pendingRejectionData.value,
        },
      });

      showRejectionDialog.value = false;
      showVerificationDialog.value = false;
      addToastSuccess({ description: "Document refusé" });
      onRejected?.(documentId);
      resetFlow();
    } catch (error) {
      addToastError({ title: "Une erreur est survenue lors du refus du document" }, error);
    } finally {
      isRejecting.value = false;
    }
  };

  const handleConfirmClose = () => {
    showConfirmDialog.value = false;
    // Force close verification dialog
    verificationDialogRef.value?.forceClose();
    resetFlow();
  };

  const handleCancelClose = () => {
    showConfirmDialog.value = false;
  };

  return {
    // Files state
    files,
    fileLimitWarning,
    fileMimeTypeWarning,
    dismissFileLimitWarning,
    dismissFileMimeTypeWarning,

    // Extraction state
    extractedData,
    extractionError,
    isExtracting,

    // Dialog state
    showVerificationDialog,
    verificationDialogRef,

    // Storage state
    isStoring,
    isRejecting,

    // Rejection dialog state
    showRejectionDialog,
    pendingRejectionData,

    // Event handlers
    handleNewFiles,
    handleRemoveFile,
    handleVerificationComplete,
    handleVerificationReject,
    handleRejectionConfirm,
    handleRejectionCancel,
    handleVerificationRequestClose,

    // Confirmation dialog state
    showConfirmDialog,
    handleConfirmClose,
    handleCancelClose,

    // Reset
    resetFlow,

    // Verification flow
    openVerificationDialog,
    isVerificationMode,

    // Replacement flow
    setDocumentToReplace,
  };
};
